import { useState, useEffect, useMemo } from 'react'
import type { Account, AccountType, Transaction, Category, Member, AssetGoalProgress, MarketQuote, EconomicIndicator } from '@zeni/shared'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell,
  ResponsiveContainer,
} from 'recharts'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { useTheme } from '../contexts/ThemeContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { plusSign, signPrefix } from '../lib/amount-format'
import { InfoTooltip } from '../components/InfoTooltip'
import type { TranslationKey } from '../i18n/translations'
import { accountsApi, transactionsApi, categoriesApi, reportsApi, membersApi, assetGoalApi, marketApi, economicIndicatorsApi } from '../lib/api'
import { CardPaymentShortfallBanner } from '../components/CardPaymentForecastPanel'
import { btnDanger, btnIcon, btnPrimary, btnSecondary, modalTitle, pageSubtitle, pageTitle, sectionTitle, segGroup, segItem } from '../lib/ui'

interface AssetTrendMonth {
  year: number
  month: number
  totalAssets: number
}

interface MonthlySummary {
  year: number
  month: number
  income: number
  expense: number
  balance: number
  previousMonth: { income: number; expense: number; balance: number }
  expenseChangeRate: number
}

function SummaryCard({
  title,
  value,
  sub,
  colorClass,
}: {
  title: string
  value: string
  sub?: string
  colorClass?: string
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
      <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
      <p className={`mt-2 text-2xl font-semibold ${colorClass ?? 'text-gray-900 dark:text-gray-100'}`}>{value}</p>
      {sub && <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{sub}</p>}
    </div>
  )
}

function MarketQuoteCard({
  title,
  quote,
  formatPrice,
  changeFractionDigits,
}: {
  title: string
  quote: MarketQuote | undefined
  formatPrice: (price: number) => string
  changeFractionDigits: number
}) {
  const changeColorClass = !quote
    ? 'text-gray-400 dark:text-gray-500'
    : quote.changeAmount > 0
    ? 'text-green-600'
    : quote.changeAmount < 0
    ? 'text-red-600'
    : 'text-gray-500 dark:text-gray-400'

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
      <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
      <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
        {quote ? formatPrice(quote.price) : '...'}
      </p>
      <p className={`mt-1 text-xs ${changeColorClass}`}>
        {quote
          ? `${plusSign(quote.changeAmount, changeFractionDigits)}${quote.changeAmount.toFixed(changeFractionDigits)}` +
            ` (${plusSign(quote.changePercent, 2)}${quote.changePercent.toFixed(2)}%)`
          : ''}
      </p>
    </div>
  )
}

/** 出典ページへのリンク付きで、対象期間と出典元を出す一行 */
function EconomicIndicatorSource({ indicator }: { indicator: EconomicIndicator }) {
  const text = `${indicator.periodLabel}・${indicator.sourceName}`
  if (!indicator.sourceUrl) {
    return <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">{text}</p>
  }
  return (
    <a
      href={indicator.sourceUrl}
      target="_blank"
      rel="noreferrer"
      className="mt-2 block text-xs text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
    >
      {text} ↗
    </a>
  )
}

function EconomicIndicatorCard({
  title,
  indicator,
  changeLabel,
}: {
  title: string
  indicator: EconomicIndicator
  /** 増減が何との比較かを示す接頭辞（'前回比'） */
  changeLabel: string
}) {
  const { changeAmount, fractionDigits, unit, betterWhen } = indicator

  // 完全失業率のように「下がるほど良い」指標があるので、符号だけで緑/赤を決めない。
  // 良し悪しが一概に言えない物価指数などは色を付けずに増減だけ見せる
  const improved =
    changeAmount === null || changeAmount === 0 || betterWhen === 'neutral'
      ? null
      : betterWhen === 'higher'
      ? changeAmount > 0
      : changeAmount < 0
  const changeColorClass =
    improved === null ? 'text-gray-500 dark:text-gray-400' : improved ? 'text-green-600' : 'text-red-600'

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
      <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
      <p className="mt-2 text-2xl font-semibold text-gray-900 dark:text-gray-100">
        {indicator.value !== null ? `${indicator.value.toFixed(fractionDigits)}${unit}` : '—'}
      </p>
      <p className={`mt-1 text-xs ${changeColorClass}`}>
        {changeAmount !== null
          ? `${changeLabel} ${signPrefix(changeAmount, fractionDigits)}${Math.abs(changeAmount).toFixed(fractionDigits)}`
          : ' '}
      </p>
      <EconomicIndicatorSource indicator={indicator} />
    </div>
  )
}

/** 月例経済報告のように、数値ではなく文章が主役の指標 */
function EconomicAssessmentCard({ title, indicator }: { title: string; indicator: EconomicIndicator }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
      <p className="text-sm text-gray-500 dark:text-gray-400">{title}</p>
      <p className="mt-2 text-base font-medium leading-relaxed text-gray-900 dark:text-gray-100">
        {indicator.assessment ?? '—'}
      </p>
      <EconomicIndicatorSource indicator={indicator} />
    </div>
  )
}

/** 持ち主が未設定の口座をまとめる仮想行の ID。実在するメンバー ID と衝突しない値にする */
const UNASSIGNED_MEMBER_ID = '__unassigned__'

const UNASSIGNED_MEMBER_COLOR = '#9ca3af'

const DEFAULT_MEMBER_COLOR = '#6366f1'

/** 総資産に対する構成比。総資産が 0 以下だと比率が意味を持たないので「-」にする */
function formatShare(value: number, total: number): string {
  if (total <= 0) return '-'
  return `${Math.round((value / total) * 100)}`
}

/** 資産構成の内訳の単位。口座の種別を家計簿として意味のある粒度にまとめたもの */
type AssetGroup = 'bank' | 'investment' | 'cash' | 'credit_card' | 'other'

const ASSET_GROUP_ORDER: AssetGroup[] = ['bank', 'investment', 'cash', 'credit_card', 'other']

const ASSET_GROUP_TITLE_KEYS: Record<AssetGroup, TranslationKey> = {
  bank: 'accounts_section_bank',
  investment: 'accounts_section_investment',
  cash: 'accounts_section_cash',
  credit_card: 'account_type_credit_card',
  other: 'accounts_section_other',
}

const ASSET_GROUP_COLORS: Record<AssetGroup, string> = {
  bank: '#6366f1',
  investment: '#10b981',
  cash: '#f59e0b',
  credit_card: '#ef4444',
  other: '#94a3b8',
}

function assetGroupOf(type: AccountType): AssetGroup {
  switch (type) {
    case 'checking':
    case 'savings':
      return 'bank'
    case 'investment':
      return 'investment'
    case 'cash':
      return 'cash'
    case 'credit_card':
      return 'credit_card'
    default:
      return 'other'
  }
}

/** 1 年の平均日数。達成までの日数を年に直すのに使う */
const DAYS_PER_YEAR = 365.2425

/** 達成見込みを年数で出すのが現実的な上限。これを超えたら「n年以上」とだけ言う */
const GOAL_YEARS_CAP = 50

/** 残高を隠しているときに金額の代わりに出す文字列 */
const HIDDEN_AMOUNT = '¥---,---'

/** 進捗のドーナツ。値は 0〜1 */
function GoalDonut({ progress, isDark }: { progress: number; isDark: boolean }) {
  const radius = 45
  const circumference = 2 * Math.PI * radius
  return (
    <div className="relative w-32 h-32 shrink-0">
      <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
        <circle
          cx="60" cy="60" r={radius} fill="none" strokeWidth="12"
          stroke={isDark ? '#374151' : '#e5e7eb'}
        />
        <circle
          cx="60" cy="60" r={radius} fill="none" strokeWidth="12"
          stroke="#6366f1"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - progress)}
        />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-2xl font-bold text-gray-900 dark:text-gray-100">
        {Math.round(progress * 100)}%
      </span>
    </div>
  )
}

/**
 * 見込みの根拠。ツールチップの中身として出す。
 * 数字はすべて API が返した計算結果で、画面側では単位を整えるだけにしている
 */
function GoalBasis({ goal, maskedAmount }: { goal: AssetGoalProgress; maskedAmount: string | null }) {
  const { t } = useLanguage()
  const { formatJPY, formatSignedJPY } = useAmountFormat()
  const { projection } = goal

  const amount = (value: number) => maskedAmount ?? formatJPY(value)
  const perMonth = (value: number) =>
    t('dashboard_goal_pace_monthly').replace(
      '{amount}',
      maskedAmount ?? `${plusSign(value)}${formatSignedJPY(value)}`,
    )

  const rows: { label: string; value: string; note?: string }[] = [
    { label: t('dashboard_goal_basis_pace').replace('{months}', String(goal.paceMonths)), value: perMonth(goal.monthlyPace) },
    { label: t('dashboard_goal_basis_cashflow'), value: perMonth(projection.monthlyCashflow) },
    { label: t('dashboard_goal_basis_fit'), value: projection.fitQuality.toFixed(2) },
    { label: t('dashboard_goal_basis_deviation'), value: amount(projection.monthlyStdDev) },
    ...projection.outlook.map((o) => ({
      label: t('dashboard_goal_basis_outlook').replace('{years}', String(Math.round(o.months / 12))),
      value: amount(o.amount),
      note: t('dashboard_goal_basis_range')
        .replace('{low}', amount(o.low))
        .replace('{high}', amount(o.high)),
    })),
  ]

  return (
    <div>
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-200">{t('dashboard_goal_basis')}</p>
      <dl className="mt-2 space-y-1.5">
        {rows.map((row) => (
          <div key={row.label}>
            <div className="flex items-baseline justify-between gap-2">
              <dt className="text-xs text-gray-500 dark:text-gray-400">{row.label}</dt>
              <dd className="text-xs font-medium text-gray-900 tabular-nums dark:text-gray-100">{row.value}</dd>
            </div>
            {row.note && <p className="text-[11px] text-right text-gray-400 dark:text-gray-500">{row.note}</p>}
          </div>
        ))}
      </dl>
      <p className="mt-2 border-t border-gray-100 pt-2 text-[11px] leading-relaxed text-gray-400 dark:border-gray-700 dark:text-gray-500">
        {t('dashboard_goal_basis_note')
          .replace('{months}', String(goal.paceMonths))
          .replace('{points}', String(goal.paceMonths + 1))}
      </p>
    </div>
  )
}

interface GoalModalProps {
  token: string
  /** 編集開始時の目標金額。未設定なら null */
  initialAmount: number | null
  paceMonths: number
  onClose: () => void
  onSaved: () => void
}

/**
 * 目標金額の設定。開いている間だけマウントするので、
 * 前回開いたときの入力値は持ち越さない
 */
function GoalModal({ token, initialAmount, paceMonths, onClose, onSaved }: GoalModalProps) {
  const { t } = useLanguage()
  const { formatJPY } = useAmountFormat()
  const [amount, setAmount] = useState(initialAmount === null ? '' : String(initialAmount))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const parsed = Number(amount)
  const valid = amount !== '' && Number.isFinite(parsed) && parsed > 0

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!valid) return
    setSubmitting(true)
    setError(null)
    try {
      await assetGoalApi.save(token, { targetAmount: parsed })
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm(t('dashboard_goal_delete_confirm'))) return
    setSubmitting(true)
    setError(null)
    try {
      await assetGoalApi.delete(token)
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-5 dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
        <h2 className={`${modalTitle} mb-4`}>
          {initialAmount === null ? t('dashboard_goal_set') : t('dashboard_goal_edit')}
        </h2>
        {error && <p className="text-red-600 text-sm mb-3 dark:text-red-400">{error}</p>}
        <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t('dashboard_goal_amount_label')}
            </label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min={1}
              // step を刻むと HTML の検証が min からの倍数しか受け付けなくなり、
              // min=1 / step=10000 では 12,000,000 が弾かれる。任意の金額を許す
              step="any"
              autoFocus
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              required
            />
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              {valid ? formatJPY(parsed) : t('dashboard_goal_amount_hint').replace('{months}', String(paceMonths))}
            </p>
          </div>
          <div className="flex justify-between gap-3 pt-2">
            {initialAmount === null ? <span /> : (
              <button
                type="button"
                onClick={() => { void handleDelete() }}
                disabled={submitting}
                className={btnDanger}
              >
                {t('dashboard_goal_delete')}
              </button>
            )}
            <div className="flex gap-3">
              <button type="button" onClick={onClose} className={btnSecondary}>
                {t('common_cancel')}
              </button>
              <button type="submit" disabled={submitting || !valid} className={btnPrimary}>
                {submitting ? t('common_saving') : t('common_save')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

function categoryName(categories: Category[], id: string): string {
  return categories.find((c) => c.id === id)?.name ?? id
}

export default function Dashboard() {
  const { isDark } = useTheme()
  const { accessToken } = useAuth()
  const { t, lang } = useLanguage()
  const { unit, formatJPY, formatSignedJPY, formatCurrency } = useAmountFormat()
  const now = new Date()
  const year = now.getFullYear()
  const month = now.getMonth() + 1

  const [summary, setSummary] = useState<MonthlySummary | null>(null)
  const [summaryLoading, setSummaryLoading] = useState(true)
  const [summaryError, setSummaryError] = useState<string | null>(null)

  const [accounts, setAccounts] = useState<Account[]>([])
  const [accountsLoading, setAccountsLoading] = useState(true)

  const [members, setMembers] = useState<Member[]>([])

  const [recentTx, setRecentTx] = useState<Transaction[]>([])
  const [txLoading, setTxLoading] = useState(true)
  const [txError, setTxError] = useState<string | null>(null)

  const [categories, setCategories] = useState<Category[]>([])

  const [assetTrend, setAssetTrend] = useState<AssetTrendMonth[]>([])
  const [trendMonths, setTrendMonths] = useState<6 | 12>(12)
  const [trendLoading, setTrendLoading] = useState(true)

  const [todayNetChange, setTodayNetChange] = useState<number | null>(null)
  const [balanceVisible, setBalanceVisible] = useState(true)
  const hiddenPlaceholder = HIDDEN_AMOUNT

  const [goal, setGoal] = useState<AssetGoalProgress | null>(null)
  const [goalLoading, setGoalLoading] = useState(true)
  const [goalModalOpen, setGoalModalOpen] = useState(false)
  // 保存・削除のあとに達成見込みを取り直すためのカウンタ
  const [goalReloadKey, setGoalReloadKey] = useState(0)

  const [marketQuotes, setMarketQuotes] = useState<MarketQuote[]>([])
  const [marketError, setMarketError] = useState<string | null>(null)

  const [econIndicators, setEconIndicators] = useState<EconomicIndicator[]>([])
  const [econError, setEconError] = useState<string | null>(null)

  // Fetch monthly summary whenever year/month changes
  useEffect(() => {
    if (!accessToken) return
    setSummaryLoading(true)
    setSummaryError(null)
    reportsApi.monthlySummary(accessToken, year, month)
      .then((res) => setSummary(res.data))
      .catch((err: unknown) => setSummaryError(err instanceof Error ? err.message : t('common_fetch_error')))
      .finally(() => setSummaryLoading(false))
  }, [accessToken, year, month, t])

  // Fetch accounts once
  useEffect(() => {
    if (!accessToken) return
    setAccountsLoading(true)
    accountsApi.list(accessToken, { includeArchived: false })
      .then((res) => setAccounts(res.data))
      .catch(() => undefined)
      .finally(() => setAccountsLoading(false))
  }, [accessToken])

  // Fetch members once (メンバー別資産の内訳に使う)
  useEffect(() => {
    if (!accessToken) return
    membersApi.list(accessToken)
      .then((res) => setMembers(res.data))
      .catch(() => undefined)
  }, [accessToken])

  // Fetch recent transactions and categories once
  useEffect(() => {
    if (!accessToken) return
    setTxLoading(true)
    transactionsApi.list(accessToken, { limit: 10 })
      .then((res) => setRecentTx(res.data))
      .catch((err: unknown) => setTxError(err instanceof Error ? err.message : t('common_fetch_error')))
      .finally(() => setTxLoading(false))
    categoriesApi.list(accessToken)
      .then((res) => setCategories(res.data))
      .catch(() => undefined)
  }, [accessToken])

  // Fetch today's transactions to compute day-over-day asset change
  useEffect(() => {
    if (!accessToken) return
    const today = new Date()
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    transactionsApi.list(accessToken, { dateFrom: todayStr, dateTo: todayStr, limit: 1000 })
      .then((res) => {
        // 振替は口座間の移動なので総資産は変わらない（収支計算からも除外する）。
        // 集計対象外の取引も、残高には効くが収支としては数えない
        const net = res.data.reduce(
          (sum, tx) => tx.type === 'transfer' || tx.excluded ? sum : sum + (tx.type === 'income' ? tx.amount : -tx.amount),
          0,
        )
        setTodayNetChange(net)
      })
      .catch(() => setTodayNetChange(0))
  }, [accessToken])

  // Fetch asset trend
  useEffect(() => {
    if (!accessToken) return
    setTrendLoading(true)
    const toDate = new Date()
    const fromDate = new Date(toDate.getFullYear(), toDate.getMonth() - (trendMonths - 1), 1)
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    reportsApi.assetTrend(accessToken, fmt(fromDate), fmt(toDate))
      .then((res) => setAssetTrend(res.data.months))
      .catch(() => undefined)
      .finally(() => setTrendLoading(false))
  }, [accessToken, trendMonths])

  // Fetch goal progress. 「今日」は端末の日付を渡す（API は UTC で動く）
  useEffect(() => {
    if (!accessToken) return
    setGoalLoading(true)
    const d = new Date()
    const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    assetGoalApi.get(accessToken, todayStr)
      .then((res) => setGoal(res.data))
      .catch(() => undefined)
      .finally(() => setGoalLoading(false))
  }, [accessToken, goalReloadKey])

  // Fetch market quotes (S&P500・ドル円)。ユーザーの資産とは無関係の一般的な相場情報
  useEffect(() => {
    if (!accessToken) return
    marketApi.quotes(accessToken)
      .then((res) => { setMarketQuotes(res.data); setMarketError(null) })
      .catch((err) => setMarketError(err instanceof Error ? err.message : String(err)))
  }, [accessToken])

  // Fetch Japanese economic indicators。月次・四半期公表なので初回だけ取れば足りる
  useEffect(() => {
    if (!accessToken) return
    economicIndicatorsApi.list(accessToken)
      .then((res) => { setEconIndicators(res.data); setEconError(null) })
      .catch((err) => setEconError(err instanceof Error ? err.message : String(err)))
  }, [accessToken])

  const sp500Quote = marketQuotes.find((q) => q.symbol === 'SP500')
  const usdJpyQuote = marketQuotes.find((q) => q.symbol === 'USDJPY')

  const totalAssets = accounts.reduce((sum, a) => sum + a.balance, 0)

  // メンバー別の資産。総資産と同じ計算（クレジットカードの利用額も差し引く）を
  // 持ち主ごとに分けたもの。持ち主が未設定の口座は末尾にまとめる
  const memberAssets = useMemo(() => {
    const rows = members.map((m) => ({
      id: m.id,
      name: m.name,
      color: m.color ?? DEFAULT_MEMBER_COLOR,
      total: accounts
        .filter((a) => a.memberId === m.id)
        .reduce((sum, a) => sum + a.balance, 0),
    }))
    const unassigned = accounts.filter((a) => !a.memberId)
    if (unassigned.length > 0) {
      rows.push({
        id: UNASSIGNED_MEMBER_ID,
        name: t('dashboard_member_unassigned'),
        color: UNASSIGNED_MEMBER_COLOR,
        total: unassigned.reduce((sum, a) => sum + a.balance, 0),
      })
    }
    return rows
  }, [members, accounts, t])

  // 資産構成。総資産と同じ計算を口座の種別ごとに分けたもの。
  // 残高 0 の種別は内訳として意味がないので出さない
  const assetComposition = useMemo(() => {
    const totals = new Map<AssetGroup, number>()
    for (const a of accounts) {
      const group = assetGroupOf(a.type)
      totals.set(group, (totals.get(group) ?? 0) + a.balance)
    }
    return ASSET_GROUP_ORDER
      .map((group) => ({
        group,
        name: t(ASSET_GROUP_TITLE_KEYS[group]),
        color: ASSET_GROUP_COLORS[group],
        total: totals.get(group) ?? 0,
      }))
      .filter((row) => row.total !== 0)
  }, [accounts, t])

  // 円グラフはマイナス残高（クレジットカードの利用額など）を扇形にできないので、
  // プラスの種別だけを描く。マイナスの種別は下の一覧に金額と比率で出す
  const assetCompositionSlices = assetComposition.filter((row) => row.total > 0)

  const hasAssignedAccount = accounts.some((a) => a.memberId)

  // 資産が増えているか。増えていないときは達成日ではなく増減ペースを主役にする
  const goalIsGrowing = (goal?.monthlyPace ?? 0) > 0 || goal?.daysToGoal === 0

  /**
   * 大きく出す 1 行。
   * 増えていれば達成までの期間、増えていなければ毎月いくら動いているか
   */
  const goalPaceText = ((): string | null => {
    if (!goal?.goal) return null
    const days = goal.daysToGoal
    if (days === 0) return t('dashboard_goal_achieved')
    if (days === null) {
      if (!balanceVisible) return hiddenPlaceholder
      return t('dashboard_goal_pace_monthly').replace(
        '{amount}',
        `${plusSign(goal.monthlyPace)}${formatSignedJPY(goal.monthlyPace)}`,
      )
    }
    const years = days / DAYS_PER_YEAR
    if (years >= GOAL_YEARS_CAP) return t('dashboard_goal_over_years').replace('{years}', String(GOAL_YEARS_CAP))
    // 1 年を超えると日数では規模が掴めないので年で言う
    if (days < 365) return t('dashboard_goal_days').replace('{days}', days.toLocaleString())
    return t('dashboard_goal_years').replace('{years}', years.toFixed(1))
  })()

  /** 小さく出す補足。達成予定日（増えていないときは直近の見込み額）と確度 */
  const goalSubText = ((): string | null => {
    if (!goal?.goal || goal.daysToGoal === 0) return null
    const confidence = t('dashboard_goal_confidence')
      .replace('{percent}', String(Math.round(goal.projection.confidence * 100)))
    const nearest = goal.projection.outlook[0]
    const outlook =
      goal.daysToGoal !== null && goal.estimatedDate
        ? t('dashboard_goal_estimated_date').replace('{date}', goal.estimatedDate)
        : nearest
        ? t('dashboard_goal_outlook_short')
            .replace('{years}', String(Math.round(nearest.months / 12)))
            .replace('{amount}', balanceVisible ? formatJPY(nearest.amount) : hiddenPlaceholder)
        : null
    if (!outlook) return confidence
    return `${outlook}${lang === 'ja' ? '・' : ' · '}${confidence}`
  })()

  const balanceColorClass = summary
    ? summary.balance >= 0 ? 'text-green-600' : 'text-red-600'
    : 'text-gray-900'

  const balanceSign = summary ? plusSign(summary.balance) : ''

  return (
    <div>
      <h1 className={pageTitle}>{t('dashboard_title')}</h1>
      <p className={pageSubtitle}>{t('dashboard_subtitle')}</p>

      {/* カードの引き落としに残高が足りない見込みのときだけ出る警告 */}
      <CardPaymentShortfallBanner token={accessToken ?? ''} />

      {/* Total assets banner */}
      <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-start justify-between gap-4">
          <p className="text-base text-gray-700 dark:text-gray-300">{t('dashboard_total_assets')}</p>
          <button
            type="button"
            onClick={() => setBalanceVisible((v) => !v)}
            aria-label={balanceVisible ? 'Hide balance' : 'Show balance'}
            className={btnIcon}
          >
            {balanceVisible ? (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
              </svg>
            )}
          </button>
        </div>
        <div className="mt-1 flex flex-col items-end">
          <p
            className={`text-3xl sm:text-4xl font-light tracking-tight ${
              !balanceVisible
                ? 'text-gray-400 dark:text-gray-500'
                : totalAssets < 0
                ? 'text-red-600'
                : 'text-gray-900 dark:text-gray-100'
            }`}
          >
            {accountsLoading ? '...' : balanceVisible ? formatJPY(totalAssets) : hiddenPlaceholder}
          </p>
          {todayNetChange !== null && (
            <div
              className={`mt-2 flex items-center gap-2 text-sm ${
                !balanceVisible
                  ? 'text-gray-400 dark:text-gray-500'
                  : todayNetChange < 0
                  ? 'text-red-500'
                  : 'text-cyan-500'
              }`}
            >
              <span>{t('dashboard_daily_change')}</span>
              <span>
                {balanceVisible
                  ? `${plusSign(todayNetChange)}${formatJPY(todayNetChange)}`
                  : hiddenPlaceholder}
              </span>
              {balanceVisible && (
                <span
                  className={`inline-flex items-center justify-center w-5 h-5 rounded-full text-white ${
                    todayNetChange < 0 ? 'bg-red-500' : 'bg-cyan-500'
                  }`}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {todayNetChange < 0 ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 17H7m0 0V7m0 10l10-10" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 17L17 7m0 0H7m10 0v10" />
                    )}
                  </svg>
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Market quotes (S&P500・ドル円) */}
      <div className="mt-4">
        <h2 className={sectionTitle}>{t('dashboard_market_title')}</h2>
        {marketError ? (
          <p className="mt-2 text-sm text-red-600">{t('dashboard_market_error')}</p>
        ) : (
          <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MarketQuoteCard
              title={t('dashboard_market_sp500')}
              quote={sp500Quote}
              changeFractionDigits={2}
              formatPrice={(price) =>
                new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(price)
              }
            />
            <MarketQuoteCard
              title={t('dashboard_market_usdjpy')}
              quote={usdJpyQuote}
              changeFractionDigits={2}
              formatPrice={(price) => `${price.toFixed(2)}${lang === 'ja' ? '円' : ' JPY'}`}
            />
          </div>
        )}
      </div>

      {/* Japanese economic indicators (月例経済報告・GDP・CPI など) */}
      <div className="mt-4">
        <div className="flex items-center gap-1.5">
          <h2 className={sectionTitle}>{t('dashboard_econ_title')}</h2>
          <InfoTooltip label={t('dashboard_econ_about')}>
            <p className="text-xs text-gray-600 dark:text-gray-300">{t('dashboard_econ_about_body')}</p>
          </InfoTooltip>
        </div>
        {econError ? (
          <p className="mt-2 text-sm text-red-600">{t('dashboard_econ_error')}</p>
        ) : (
          <>
            {econIndicators
              .filter((i) => i.kind === 'text')
              .map((indicator) => (
                <div key={indicator.key} className="mt-2">
                  <EconomicAssessmentCard
                    title={t(`econ_${indicator.key}` as TranslationKey)}
                    indicator={indicator}
                  />
                </div>
              ))}
            <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {econIndicators
                .filter((i) => i.kind === 'numeric')
                .map((indicator) => (
                  <EconomicIndicatorCard
                    key={indicator.key}
                    title={t(`econ_${indicator.key}` as TranslationKey)}
                    indicator={indicator}
                    changeLabel={t('dashboard_econ_change')}
                  />
                ))}
            </div>
          </>
        )}
      </div>

      {/* Goal progress */}
      <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
        <div className="flex items-start justify-between gap-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard_goal_title')}</p>
          <button
            type="button"
            onClick={() => setGoalModalOpen(true)}
            aria-label={goal?.goal ? t('dashboard_goal_edit') : t('dashboard_goal_set')}
            className={btnIcon}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
        </div>

        {goalLoading ? (
          <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">{t('common_loading')}</p>
        ) : !goal?.goal ? (
          <div className="mt-4">
            <p className="text-sm text-gray-400 dark:text-gray-500">{t('dashboard_goal_empty')}</p>
            <button
              type="button"
              onClick={() => setGoalModalOpen(true)}
              className={`${btnPrimary} mt-3`}
            >
              {t('dashboard_goal_set')}
            </button>
          </div>
        ) : (
          <div className="mt-4 flex flex-col items-center gap-4 sm:flex-row">
            <GoalDonut progress={goal.progress} isDark={isDark} />
            <div className="w-full">
              <div className="flex items-center justify-end gap-1.5">
                <p className="text-sm text-gray-400 dark:text-gray-500">
                  {goalIsGrowing ? t('dashboard_goal_pace_label') : t('dashboard_goal_pace_label_decreasing')}
                </p>
                <InfoTooltip label={t('dashboard_goal_basis')}>
                  <GoalBasis goal={goal} maskedAmount={balanceVisible ? null : hiddenPlaceholder} />
                </InfoTooltip>
              </div>
              <p className="mt-1 text-xl sm:text-2xl font-bold text-right text-gray-900 dark:text-gray-100">
                {goalPaceText}
              </p>
              {goalSubText && (
                <p className="mt-1 text-xs text-right text-gray-400 dark:text-gray-500">{goalSubText}</p>
              )}
              <dl className="mt-4 pt-4 border-t border-gray-100 space-y-2 dark:border-gray-800">
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard_goal_remaining')}</dt>
                  <dd className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {balanceVisible ? formatJPY(goal.remainingAmount) : hiddenPlaceholder}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-3">
                  <dt className="text-sm text-gray-500 dark:text-gray-400">{t('dashboard_goal_target')}</dt>
                  <dd className="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {balanceVisible ? formatJPY(goal.goal.targetAmount) : hiddenPlaceholder}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </div>

      {goalModalOpen && accessToken && (
        <GoalModal
          token={accessToken}
          initialAmount={goal?.goal?.targetAmount ?? null}
          paceMonths={goal?.paceMonths ?? 12}
          onClose={() => setGoalModalOpen(false)}
          onSaved={() => setGoalReloadKey((k) => k + 1)}
        />
      )}

      {/* Monthly summary cards */}
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        {summaryLoading ? (
          <>
            <SummaryCard title={t('dashboard_month_income')} value="..." />
            <SummaryCard title={t('dashboard_month_expense')} value="..." />
            <SummaryCard title={t('dashboard_month_balance')} value="..." />
          </>
        ) : summaryError ? (
          <div className="col-span-3 flex items-center">
            <p className="text-sm text-red-600">{summaryError}</p>
          </div>
        ) : summary ? (
          <>
            <SummaryCard
              title={t('dashboard_month_income')}
              value={formatJPY(summary.income)}
              colorClass="text-green-600"
            />
            <SummaryCard
              title={t('dashboard_month_expense')}
              value={formatJPY(summary.expense)}
              colorClass="text-red-600"
            />
            <SummaryCard
              title={t('dashboard_month_balance')}
              value={`${balanceSign}${formatJPY(summary.balance)}`}
              colorClass={balanceColorClass}
            />
          </>
        ) : null}
      </div>

      {/* Asset trend chart */}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className={sectionTitle}>{t('dashboard_asset_trend')}</h2>
          <div className={segGroup}>
            {([6, 12] as const).map((n) => (
              <button
                key={n}
                onClick={() => setTrendMonths(n)}
                className={segItem(trendMonths === n)}
              >
                {t('dashboard_asset_trend_months').replace('{n}', String(n))}
              </button>
            ))}
          </div>
        </div>
        <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
          {trendLoading ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">{t('common_loading')}</div>
          ) : assetTrend.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">{t('dashboard_no_tx')}</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={assetTrend.map((m) => ({
                label: `${m.year}/${String(m.month).padStart(2, '0')}`,
                assets: m.totalAssets,
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#f0f0f0'} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => {
                    if (unit === 'man' || lang === 'ja') {
                      if (v >= 10000) return `${Math.round(v / 10000)}万`
                    } else if (v >= 1000) {
                      return `${Math.round(v / 1000)}k`
                    }
                    return v.toLocaleString()
                  }}
                  width={56}
                />
                <Tooltip
                  formatter={(value) => [formatJPY(Number(value)), t('dashboard_total_assets')]}
                  contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`, background: isDark ? '#111827' : '#fff' }}
                />
                <Line
                  type="monotone"
                  dataKey="assets"
                  stroke="#6366f1"
                  strokeWidth={2}
                  dot={{ r: 3, fill: '#6366f1' }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Asset breakdown by account type */}
      <div className="mt-8">
        <h2 className={sectionTitle}>{t('dashboard_asset_composition')}</h2>
        <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800">
          {accountsLoading ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">{t('common_loading')}</div>
          ) : assetComposition.length === 0 ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400 dark:text-gray-500">{t('accounts_no_result')}</div>
          ) : (
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              {assetCompositionSlices.length > 0 && (
                <div className="w-full sm:w-1/2 shrink-0">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie
                        data={assetCompositionSlices}
                        dataKey="total"
                        nameKey="name"
                        innerRadius={55}
                        outerRadius={90}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {assetCompositionSlices.map((row) => (
                          <Cell key={row.group} fill={row.color} />
                        ))}
                      </Pie>
                      {balanceVisible && (
                        <Tooltip
                          formatter={(value, name) => [formatJPY(Number(value)), String(name)]}
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`, background: isDark ? '#111827' : '#fff' }}
                        />
                      )}
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}
              <ul className="w-full space-y-3">
                {assetComposition.map((row) => (
                  <li key={row.group} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: row.color }} />
                      <span className="text-sm text-gray-600 truncate dark:text-gray-300">{row.name}</span>
                    </div>
                    <div className="text-right shrink-0">
                      <p
                        className={`text-sm font-semibold ${
                          !balanceVisible
                            ? 'text-gray-400 dark:text-gray-500'
                            : row.total < 0
                            ? 'text-red-600'
                            : 'text-gray-900 dark:text-gray-100'
                        }`}
                      >
                        {balanceVisible ? formatJPY(row.total) : hiddenPlaceholder}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {t('dashboard_asset_share').replace('{percent}', formatShare(row.total, totalAssets))}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Assets by member */}
      {members.length > 0 && (
        <div className="mt-8">
          <h2 className={sectionTitle}>{t('dashboard_member_assets')}</h2>
          {accountsLoading ? (
            <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">{t('common_loading')}</p>
          ) : !hasAssignedAccount ? (
            <p className="mt-4 text-sm text-gray-400 dark:text-gray-500">{t('dashboard_member_assets_empty')}</p>
          ) : (
            <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {memberAssets.map((row) => (
                <div
                  key={row.id}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 dark:bg-gray-900 dark:border-gray-800"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: row.color }} />
                    <p className="text-sm text-gray-500 truncate dark:text-gray-400">{row.name}</p>
                  </div>
                  <p
                    className={`mt-2 text-2xl font-semibold ${
                      !balanceVisible
                        ? 'text-gray-400 dark:text-gray-500'
                        : row.total < 0
                        ? 'text-red-600'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}
                  >
                    {balanceVisible ? formatJPY(row.total) : hiddenPlaceholder}
                  </p>
                  <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                    {balanceVisible
                      ? t('dashboard_member_share').replace('{percent}', formatShare(row.total, totalAssets))
                      : '\u00a0'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent transactions */}
      <div className="mt-8">
        <h2 className={sectionTitle}>{t('dashboard_recent_tx')}</h2>
        <div className="mt-4 bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto dark:bg-gray-900 dark:border-gray-800">
          {txLoading ? (
            <div className="p-4 text-center text-gray-400 text-sm dark:text-gray-500">{t('common_loading')}</div>
          ) : txError ? (
            <div className="p-4 text-center text-red-600 text-sm dark:text-red-400">{txError}</div>
          ) : recentTx.length === 0 ? (
            <div className="p-4 text-center text-gray-400 text-sm dark:text-gray-500">{t('dashboard_no_tx')}</div>
          ) : (
            <table className="w-full min-w-[560px]">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('transactions_col_date')}</th>
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('transactions_col_description')}</th>
                  <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('transactions_col_category')}</th>
                  <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-500 uppercase tracking-wider dark:text-gray-400">{t('transactions_col_amount')}</th>
                </tr>
              </thead>
              <tbody>
                {recentTx.map((t) => {
                  const isExpense = t.type === 'expense'
                  const isTransfer = t.type === 'transfer'
                  const formattedAmount = formatCurrency(t.amount, t.currency)
                  return (
                    <tr key={t.id} className="border-t border-gray-100 hover:bg-gray-50 dark:border-gray-800 dark:hover:bg-gray-800">
                      <td className="px-3 py-1.5 text-sm text-gray-600 whitespace-nowrap dark:text-gray-300">{t.date}</td>
                      <td className="px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100">{t.description}</td>
                      <td className="px-3 py-1.5 text-sm text-gray-500 dark:text-gray-400">{categoryName(categories, t.category)}</td>
                      <td
                        className={`px-3 py-1.5 text-sm font-medium text-right ${
                          isTransfer
                            ? 'text-gray-500 dark:text-gray-400'
                            : isExpense
                            ? 'text-red-600'
                            : 'text-green-600'
                        }`}
                      >
                        {isTransfer ? '' : isExpense ? '-' : '+'}{formattedAmount}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  )
}
